import React, { createContext, useContext, useReducer } from 'react'
import { DEFAULT_GEOFENCING } from '../civic-champs-shared/constants/GEO_DATA'
import { BROWSER_TIMEZONE } from '../constants/timezone'

interface State {
  events: any[]
}

interface EventListAction {
  type: 'registrations' | 'opportunities'
  payload: any[]
}

export type EventListDispatch = (action: EventListAction) => void

interface Props {
  children: React.ReactNode
}

const EventListStateContext = createContext<State | undefined>(undefined)
const EventListDispatchContext = createContext<EventListDispatch | undefined>(undefined)

const eventListReducer = (state: State, action: EventListAction) => {
  switch (action.type) {
    case 'registrations':
    case 'opportunities':
      const events = mapResponseToEvents(action)
      return { events }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`)
    }
  }
}

const defaultMapPosition = {
  lat: DEFAULT_GEOFENCING.location.coordinates.lat,
  lng: DEFAULT_GEOFENCING.location.coordinates.lng,
}

const mapPositionFromGeofencing = (geofencing: any) => {
  const coordinates = geofencing?.location?.coordinates
  if (!coordinates) {
    return defaultMapPosition
  }

  if (Array.isArray(coordinates)) {
    return {
      lat: coordinates[1],
      lng: coordinates[0],
    }
  }

  return {
    lat: coordinates.lat,
    lng: coordinates.lng,
  }
}

export const mapApiOpportunityToEventDetails = (
  event: any,
  sourceType: 'opportunity' | 'registration' = 'opportunity',
) => {
  const mainDetails = sourceType === 'registration' ? event.opportunity : event

  const {
    name,
    description,
    instructions,
    timeshifts,
    address,
    city,
    state,
    zip,
    geofencing,
    startsAt,
    endsAt,
    organization,
    opportunityId,
    status,
    locationDetails,
    locationIsAddress,
    locationAddressType,
    stableLink,
    allowMultipleRegistrations,
  } = mainDetails

  return {
    name,
    organization: {
      website: organization.website,
      name: organization.name,
      id: organization.id,
    },
    description,
    instructions,
    timeshifts,
    address,
    city,
    state,
    zip,
    mapPosition: mapPositionFromGeofencing(geofencing),
    locationDetails,
    locationIsAddress,
    locationAddressType,
    startsAt,
    endsAt,
    timeZone: organization.timeZone ? organization.timeZone : BROWSER_TIMEZONE,
    details: sourceType === 'registration' ? event.details : [],
    id: event.id,
    sourceType,
    missingRequiredMemberships: sourceType === 'registration' ? undefined : event.missingRequiredMemberships,
    opportunityId,
    status,
    stableLink,
    allowMultipleRegistrations: !!allowMultipleRegistrations,
  }
}

const mapResponseToEvents = (action: EventListAction) => {
  const { payload, type } = action
  const isRegistrationEvent = type === 'registrations'

  return payload.map((event: any) =>
    mapApiOpportunityToEventDetails(event, isRegistrationEvent ? 'registration' : 'opportunity'),
  )
}

const EventListProvider = ({ children }: Props) => {
  const [state, dispatch] = useReducer(eventListReducer, { events: [] })

  return (
    <EventListStateContext.Provider value={state}>
      <EventListDispatchContext.Provider value={dispatch}>{children}</EventListDispatchContext.Provider>
    </EventListStateContext.Provider>
  )
}

const useEventListState = () => {
  const context = useContext(EventListStateContext)

  if (context === undefined) {
    throw new Error('useEventListState must be used within an EventListProvider')
  }

  return context
}

const useEventListDispatch = () => {
  const context = useContext(EventListDispatchContext)

  if (context === undefined) {
    throw new Error('useEventListDispatch must be used within an EventListProvider')
  }

  return context
}

const withEventListDispatch = (Component: React.ComponentType<any>) => {
  const EventListHooksContainer = (props: any) => {
    const dispatch = useEventListDispatch()

    return <Component eventListDispatch={dispatch} {...props} />
  }

  return EventListHooksContainer
}

export { EventListProvider, useEventListState, useEventListDispatch, withEventListDispatch }
